<template>
  <section class="day-editor" :class="{ 'day-editor--editing': isEditing, 'day-editor--new': !value.id, 'day-editor--reset': activityChanged }">
    <div class="day-editor__heading">
      <div class="day-editor__summary">
        <strong class="day-editor__date">Day {{ index + 1 }} | {{ displayDate }}</strong>
        <span v-if="selectedType" class="day-editor__type" :style="{ backgroundColor: selectedType.color || '#f49a08' }">{{ selectedType.name }}</span>
        <span v-if="value.national_park" class="day-editor__park">
          {{ value.national_park.name }}<span v-if="value.national_park_area"> ({{ value.national_park_area.name }})</span>
        </span>
        <span v-if="showOperations && hasOperations" class="day-editor__prepared">
          {{ value.notes ? 'Notes' : '' }}{{ value.notes && (value.crater_transit || value.timings?.length) ? ' · ' : '' }}{{ value.crater_transit ? 'Crater transit' : '' }}{{ value.crater_transit && value.timings?.length ? ' · ' : '' }}{{ value.timings?.length ? `${value.timings.length} timings` : '' }}
        </span>
      </div>
      <div class="day-editor__actions">
        <button type="button" class="day-editor__insert" :aria-label="`Insert day before Day ${index + 1}`" @click="$emit('insert-before')">+ Before</button>
        <button type="button" class="day-editor__icon-button day-editor__change" :aria-label="isEditing ? `Done editing Day ${index + 1}` : `Change Day ${index + 1}`" :title="isEditing ? 'Done' : 'Change'" :aria-expanded="isEditing.toString()" @click="isEditing = !isEditing">
          <svg v-if="isEditing" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="m4 10 4 4 8-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <svg v-else viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M11.8 5.2 14.8 8.2M3.5 16.5l3.8-.8L16 7a1.7 1.7 0 0 0-2.4-2.4l-8.7 8.7-.8 3.2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
        <button type="button" class="day-editor__icon-button day-editor__remove" :aria-label="`Remove Day ${index + 1}`" title="Remove" @click="$emit('remove')">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M4.5 6h11M8 6V4.5h4V6m-6.5 0 .6 10h7.8l.6-10M8 9v4m4-4v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
      </div>
    </div>
    <p v-if="activityChanged" class="day-editor__warning" role="status">Activity changed: existing notes, crater transit and timings will be cleared. Timings will be rebuilt from the new template when saved or accepted.</p>
    <div v-if="isEditing" class="day-editor__fields">
      <app-form-field label="Date" required>
        <app-date-picker :value="value.date" placeholder="Date" :clearable="false" @input="update('date', $event)" />
      </app-form-field>
      <app-form-field label="Type" required>
        <app-select :value="selectedType" :options="itineraryTypes" :clearable="false" placeholder="Type" @input="selectType" />
      </app-form-field>
      <template v-if="needsPark">
        <app-form-field label="National park">
          <app-select :value="value.national_park" :options="nationalParks" :clearable="false" placeholder="National park" @input="selectPark" />
        </app-form-field>
        <app-form-field label="Area">
          <app-select :value="value.national_park_area" :options="areaOptions" :clearable="false" placeholder="Area" :readonly="!areaOptions.length" @input="update('national_park_area', $event)" />
        </app-form-field>
      </template>
    </div>
    <div v-if="isEditing && showOperations && hasOperations" class="day-editor__operations">
      <strong>Current operational details (read only)</strong>
      <p v-if="value.notes">Notes: {{ value.notes }}</p>
      <p v-if="value.crater_transit">Crater transit</p>
      <ul v-if="value.timings && value.timings.length">
        <li v-for="(timing, timingIndex) in value.timings" :key="timing.id || timingIndex">
          {{ timing.description || 'Timing' }}
          <span v-if="timing.time"> · {{ timing.time }}</span>
          <span v-else-if="timing.start_time"> · {{ timing.start_time }}</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import { format, parseISO } from 'date-fns'

export default {
  name: 'SafariItineraryDayEditor',
  props: {
    value: { type: Object, required: true },
    index: { type: Number, required: true },
    itineraryTypes: { type: Array, default: () => [] },
    nationalParks: { type: Array, default: () => [] },
    showOperations: { type: Boolean, default: false },
  },
  data() {
    return {
      isEditing: !this.value.id,
      originalActivity: [this.value.type || this.value.itinerary_type, this.value.national_park, this.value.national_park_area].map(item => item?.id || item?.code || null),
    }
  },
  computed: {
    activityChanged() {
      const activity = [this.selectedType, this.value.national_park, this.value.national_park_area].map(item => item?.id || item?.code || null)
      return Boolean(this.value.id && activity.some((key, index) => key !== this.originalActivity[index]))
    },
    displayDate() {
      if (!this.value.date) return 'Choose date'
      return format(parseISO(this.value.date), 'dd MMM')
    },
    selectedType() { return this.value.type || this.value.itinerary_type || null },
    needsPark() { return !['rest_day', 'empty_run'].includes(this.selectedType?.code) },
    areaOptions() {
      const park = this.nationalParks.find(option => option.code === this.value.national_park?.code)
      return park?.area || this.value.national_park?.area || []
    },
    hasOperations() {
      return Boolean(this.value.notes || this.value.crater_transit || this.value.timings?.length)
    },
  },
  methods: {
    update(field, value) { this.$emit('input', { ...this.value, [field]: value }) },
    selectType(type) {
      const cleared = ['rest_day', 'empty_run'].includes(type?.code)
      this.$emit('input', {
        ...this.value,
        type,
        national_park: cleared ? null : this.value.national_park,
        national_park_area: cleared ? null : this.value.national_park_area,
      })
    },
    selectPark(national_park) {
      this.$emit('input', { ...this.value, national_park, national_park_area: null })
    },
  },
}
</script>

<style scoped>
.day-editor { padding: 12px 14px; border: 1px solid #dce2e7; border-radius: 9px; background: #f9fafb; }
.day-editor--new { border-color: #b8d992; background: #f6fbef; }
.day-editor--reset { border-color: #dbad56; }
.day-editor__warning { margin: 10px 0; padding: 10px; background: #fff5dd; color: #78551c; border-radius: 6px; font-size: 13px; line-height: 1.4; }
.day-editor + .day-editor { margin-top: 12px; }
.day-editor__heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.day-editor--editing .day-editor__heading { margin-bottom: 14px; }
.day-editor__summary { display: flex; flex: 1; align-items: center; gap: 6px; min-width: 0; white-space: nowrap; overflow: hidden; }
.day-editor__date { flex: none; padding: 9px 10px; border-radius: 8px; background: #eee; color: #181818; font-size: 13px; white-space: nowrap; }
.day-editor__type { flex: none; padding: 6px 12px; border-radius: 20px; color: #fff; font-size: 13px; font-weight: 600; }
.day-editor__park { min-width: 0; overflow: hidden; text-overflow: ellipsis; color: #202020; font-size: 14px; font-weight: 600; }
.day-editor__prepared { min-width: 0; overflow: hidden; text-overflow: ellipsis; color: #747474; font-size: 12px; }
.day-editor__actions { display: flex; flex: none; align-items: center; gap: 4px; margin-left: auto; }
.day-editor__insert, .day-editor__icon-button { border: 0; background: transparent; cursor: pointer; }
.day-editor__insert { padding: 4px; font-size: 13px; white-space: nowrap; }
.day-editor__insert { color: #438007; }
.day-editor__icon-button { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 4px; border-radius: 5px; }
.day-editor__icon-button:hover, .day-editor__icon-button:focus-visible { background: #eaf0e5; }
.day-editor__icon-button svg { width: 18px; height: 18px; }
.day-editor__change { color: #438007; font-weight: 600; }
.day-editor__remove { color: #a04c38; }
.day-editor__fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.day-editor__operations { margin-top: 14px; padding: 12px; border-radius: 6px; background: #f7f7f7; font-size: 13px; line-height: 1.5; }
.day-editor__operations p { margin: 5px 0 0; white-space: pre-wrap; }
.day-editor__operations ul { margin: 5px 0 0; padding-left: 18px; }
@media (max-width: 600px) {
  .day-editor__heading { flex-wrap: wrap; }
  .day-editor__fields { grid-template-columns: 1fr; }
}
</style>
